// pages/ProductListing.jsx
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { ShoppingCart, Eye, Tag, Gem } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import CartModal from '../components/CartModal';
import ImagePopup from '../components/ImagePopup';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { ProductContext } from '../context/ProductContext';
import SkeletonLoader from '../components/SkeletonLoader';

// New component specifically for catalog shares
const CatalogShareMetadata = ({ tokenData, shareCode }) => {
  const logoUrl = tokenData && tokenData[0]?.logo_url 
    ? `${import.meta.env.VITE_API_URL}/api/v1/${tokenData[0].logo_url}` 
    : 'https://stokkh.com/logo.png';
    
  const shopName = tokenData && tokenData[0]?.display_name 
    ? tokenData[0].display_name 
    : 'STOK Digital Menu';

  return (
    <Helmet>
      <title>{`${shopName} - Digital Menu`}</title>
      
      {/* Meta tags for better search indexing */}
      <meta name="description" content="Looking for something special? ✨ Check out our hand-picked collection just for you! New arrivals & great deals!" />
      <meta name="robots" content="follow, index" />
      
      {/* Open Graph / Facebook - Specifically for catalog shares */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={window.location.href} />
      <meta property="og:title" content="Digital Menu! ✨ Best Picks for You! 🎁" />
      <meta property="og:description" content="Looking for something special? ✨ Check out our hand-picked collection just for you! 🛍️ New arrivals & great deals! 🌟" />
      <meta property="og:image" content={logoUrl} />
      
      {/* Twitter Card */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={window.location.href} />
      <meta property="twitter:title" content="Digital Menu! ✨ Best Picks for You! 🎁" />
      <meta property="twitter:description" content="Looking for something special? ✨ Check out our hand-picked collection just for you! 🛍️ New arrivals & great deals! 🌟" />
      <meta property="twitter:image" content={logoUrl} />
    </Helmet>
  );
};

const ProductListing = () => {
  const apiUrl = import.meta.env.VITE_API_URL;

  const location = useLocation();
  const [showCart, setShowCart] = useState(false);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return location.state?.selectedCategory ||
      sessionStorage.getItem('selectedCategory') ||
      "All Products";
  });

  // Add new state at the top of ProductListing component
  const [showOrderSuccess, setShowOrderSuccess] = useState(false);
  const [showExpiredMessage, setShowExpiredMessage] = useState(false);
  const [tokenData, settokenData] = useState(null);

  // Add success handler function
  const handleOrderSuccess = () => {
    setShowOrderSuccess(true);
  };

  // Update sessionStorage when category changes
  useEffect(() => {
    sessionStorage.setItem('selectedCategory', selectedCategory);
  }, [selectedCategory]);

  const navigate = useNavigate();
  const { cart } = useCart();
  const { share_code } = useParams();

  const baseURL = apiUrl;
  const api = axios.create({ baseURL: baseURL });

  useEffect(() => {
    api.get(`/STOK-Share/${share_code}`)
      .then(response => {
        settokenData(response.data);
      })
      .catch(error => {
        if (error.response && error.response.status === 410 || error.response.status === 404) {
          setShowExpiredMessage(true);
        }
        console.error('Error fetching data:', error);
      });
  }, [share_code]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get('/api/v1/share_catalog/catalog', {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${tokenData[0]['jwt_token']}`
          }
        });
        initializeInventory(response.data); // Use new function instead of setProducts
        setLoading(false);
      } catch (err) {
        setError('Error fetching products: ' + err.message);
        setLoading(false);
      }
    };

    fetchProducts();
  }, [tokenData, showOrderSuccess]);

  const { products, initializeInventory } = useContext(ProductContext); // Add initializeInventory

  const getImageUrl = (imagePath) => {
    return `${baseURL}/api/v1/${imagePath}`;
  };

  const getCategories = () => {
    const categories = new Set(["All Products"]);
    products.forEach(product => {
      product.data.forEach(item => {
        if (item.category_name) {
          categories.add(item.category_name);
        }
      });
    });
    return Array.from(categories);
  };

  // Update getFilteredProducts to exclude products in cart
  const getFilteredProducts = () => {
    let filtered = products.filter(product => {
      // First filter by category
      const matchesCategory = selectedCategory === "All Products" ||
        product.data.some(item => item.category_name === selectedCategory);

      // Calculate available quantity after subtracting cart items
      const availableQuantity = product.data.reduce((total, item) => {
        const cartItem = cart.find(ci =>
          ci.id === product.fashion_id &&
          ci.size === item.size_name &&
          ci.color === item.color_name
        );
        const cartQuantity = cartItem ? cartItem.quantity : 0;
        return total + (item.quantity - cartQuantity);
      }, 0);

      return matchesCategory && availableQuantity > 0;
    });

    return filtered;
  };

  // Add ref for the category scroll container
  const categoryScrollRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  if (showExpiredMessage) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm mx-auto text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full mx-auto flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900">Catalog Has Expired</h3>
          <p className="text-gray-600">This catalog is no longer available for viewing.</p>
          <button
            onClick={() => {
              setShowExpiredMessage(false);
              navigate('/');
            }}
            className="mt-4 w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  if (loading || !tokenData) {
    return <SkeletonLoader />;
  }

  const StoreHeader = () => {
    const [logoError, setLogoError] = useState(false);
    const logoUrl = `${baseURL}/api/v1/${tokenData[0]['logo_url']}`;

    const handleCategoryClick = (category) => {
      // Store current scroll position before updating category
      if (categoryScrollRef.current) {
        setScrollPosition(categoryScrollRef.current.scrollLeft);
      }
      setSelectedCategory(category);
    };

    // Use useEffect to restore scroll position after category update
    useEffect(() => {
      if (categoryScrollRef.current) {
        categoryScrollRef.current.scrollLeft = scrollPosition;
      }
    }, [selectedCategory]);

    return (
      <div className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 overflow-hidden rounded-lg">
                {!logoError ? (
                  <img
                    src={logoUrl}
                    alt="Luxury Jewelry Shop"
                    className="w-full h-full object-cover"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <div className="w-full h-full bg-black flex items-center justify-center">
                    <svg
                      viewBox="0 0 24 24"
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        d="M6 3L3 6v12l3 3h12l3-3V6l-3-3H6z M12 9l-3 3 3 3 3-3-3-3z"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-baseline gap-1">
                  <h1 className="text-lg sm:text-xl font-bold text-gray-900">{tokenData[0]['display_name']}</h1>
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 -mt-0.5">Best Picks for You</p>
              </div>
            </div>

            <button
              onClick={() => setShowCart(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-black text-white rounded-lg"
            >
              <ShoppingCart className="w-4 h-4" />
              <span className="text-xs sm:text-sm font-medium">Cart ({cart.length})</span>
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-2 sm:px-6">
          <div
            ref={categoryScrollRef}
            className="flex gap-1.5 pb-3 sm:pb-4 overflow-x-auto scrollbar-hide"
          >
            {getCategories().map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className={`
                  whitespace-nowrap px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg 
                  text-xs sm:text-sm font-medium transition-colors
                  ${selectedCategory === category
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                `}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {showExpiredMessage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm mx-auto text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full mx-auto flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Catalog Has Expired</h3>
              <p className="text-gray-600">This catalog is no longer available for viewing.</p>
              <button
                onClick={() => {
                  setShowExpiredMessage(false);
                  navigate('/'); // Optional: navigate away from expired catalog
                }}
                className="mt-4 w-full py-2 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        )}

        {showOrderSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm mx-auto text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Order Placed Successfully!</h3>
              <p className="text-gray-600">Thank you for your purchase. Your order has been received.</p>
              <button
                onClick={() => {
                  setShowOrderSuccess(false);
                }}
                className="mt-4 w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Modify the ProductCard click handler
  const handleProductClick = (product) => {
    navigate(`/product/${product.fashion_id}`, {
      state: { selectedCategory } // Pass the current category
    });
  };

  const ProductCard = ({ product }) => {
    const totalStock = product.data.reduce((sum, item) => {
      const cartItem = cart.find(ci =>
        ci.id === product.fashion_id &&
        ci.size === item.size_name &&
        ci.color === item.color_name
      );
      const cartQuantity = cartItem ? cartItem.quantity : 0;
      return sum + (item.quantity - cartQuantity);
    }, 0);

    const getPriceDisplay = (price, currency) => {
      return currency === "USD" ? `$${price.toFixed(2)}` : `៛${price.toFixed(0)}`;
    };

    return (
      <ProductContext.Provider value={{ product }}>
        <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
          <div
            className="relative aspect-square cursor-pointer"
            onClick={() => {
              setSelectedImage(product.image_url);
              setShowImagePopup(true);
            }}
          >
            <img
              src={getImageUrl(product.image_url)}
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
            />

            <div className="absolute top-2 right-2">
              <span className="px-2 py-0.5 text-[10px] font-medium bg-white/95 rounded-full shadow-sm">
                {product.quality}
              </span>
            </div>
          </div>

          <div
            className="p-2.5 sm:p-3"
            onClick={() => handleProductClick(product)}
          >
            <div className="space-y-1.5 sm:space-y-2">
              <h3 className="font-bold text-sm sm:text-base text-gray-900">
                {product.name}
              </h3>

              {product.description && (
                <p className="text-[11px] sm:text-xs text-gray-600">
                  {product.description}
                </p>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Tag className="w-3 h-3 text-gray-400" />
                  <span className="text-sm sm:text-base font-bold text-gray-900">
                    {getPriceDisplay(product.price, product.price_ccy)}
                  </span>
                </div>

                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-800'
                  }`}>
                  {totalStock} in stock
                </span>
              </div>
            </div>
          </div>
        </div>
      </ProductContext.Provider>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Render metadata specifically for catalog shares */}
      {tokenData && <CatalogShareMetadata tokenData={tokenData} shareCode={share_code} />}
      <StoreHeader />

      <div className="mx-auto px-2 sm:px-6 py-4 sm:py-6 max-w-7xl">
        <div className="mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900">
            {selectedCategory}
            <span className="text-xs sm:text-sm text-gray-500 ml-2">
              ({getFilteredProducts().length} products)
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-6">
          {getFilteredProducts().map(product => (
            <ProductCard key={product.fashion_id} product={product} />
          ))}
        </div>

        <CartModal
          showCart={showCart}
          setShowCart={setShowCart}
          cart={cart}
          getImageUrl={getImageUrl}
          jwtToken={tokenData?.[0]?.jwt_token} 
          onOrderSuccess={handleOrderSuccess}
        />

        <ImagePopup
          showImagePopup={showImagePopup}
          setShowImagePopup={setShowImagePopup}
          selectedImage={selectedImage}
          getImageUrl={getImageUrl}
        />
      </div>
    </div>
  );
};

export default ProductListing;